import { type AddressJson } from '@klear/address'
import { type OrganizationSize } from '@klear/constants'
import { COUNTRY_CODE_US, type CountryCode } from '@klear/constants/countries'
import isFunction from 'lodash/isFunction'
import isObject from 'lodash/isObject'
import { patchIsEmpty } from '@klear/schema/patch'

import type { CustomerCreateWire, CustomerUpdateWire } from '@klear/customer/customer.wire'

type CustomerAddressForm = {
  country: CountryCode
  line1: string
  line2: string
  city: string
  state: string
  postalCode: string
}

export type CustomerForm = Omit<
  CustomerCreateWire,
  | 'address'
  | 'description'
  | 'website'
  | 'dunsNumber'
  | 'lei'
  | 'ein'
  | 'vatNumber'
  | 'parentOrganization'
  | 'allianzEhid'
> & {
  address: CustomerAddressForm
  description: string
  website: string
  dunsNumber: string
  lei: string
  ein: string
  vatNumber: string
  parentOrganization: string
  allianzEhid: string
  traderId?: number
  traderName?: string
}

export type CustomerFormDirty = Partial<Record<Exclude<keyof CustomerForm, 'address'>, boolean | object>> & {
  address?: boolean | Partial<Record<keyof CustomerAddressForm, boolean>>
}

type CustomerFormSource = {
  name: string
  address: Partial<AddressJson> | { toJSON(): Partial<AddressJson> }
  description?: string | null
  size?: OrganizationSize
  website?: string | null
  dunsNumber?: string | null
  lei?: string | null
  ein?: string | null
  vatNumber?: string | null
  parentOrganization?: string | null
  allianzEhid?: string | null
  traderId?: number
  traderName?: string
}

export const EMPTY_CUSTOMER_FORM: CustomerForm = {
  address: {
    country: COUNTRY_CODE_US,
    line1: '',
    line2: '',
    city: '',
    state: '',
    postalCode: ''
  },
  name: '',
  description: '',
  website: '',
  dunsNumber: '',
  lei: '',
  ein: '',
  vatNumber: '',
  parentOrganization: '',
  allianzEhid: ''
}

function optionalText(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim()
  return trimmed ? trimmed : undefined
}

function clearableText(value: string | null | undefined): string | null {
  return optionalText(value) ?? null
}

function hasToJSON<T>(value: unknown): value is { toJSON(): T } {
  return value != null && isFunction((value as { toJSON?: unknown }).toJSON)
}

function sourceAddress(source: CustomerFormSource['address']): Partial<AddressJson> {
  return hasToJSON<Partial<AddressJson>>(source) ? source.toJSON() : source
}

function isDirty(dirty: CustomerFormDirty, key: Exclude<keyof CustomerForm, 'address'>): boolean {
  return dirty[key] === true || (dirty[key] != null && isObject(dirty[key]))
}

function isAddressDirty(dirty: CustomerFormDirty, key: keyof CustomerAddressForm): boolean {
  return dirty.address === true || (isObject(dirty.address) && dirty.address[key] === true)
}

export function customerReadToForm(source: CustomerFormSource): CustomerForm {
  const address = sourceAddress(source.address)
  return {
    ...EMPTY_CUSTOMER_FORM,
    address: {
      country: address.country ?? COUNTRY_CODE_US,
      line1: address.line1 ?? '',
      line2: address.line2 ?? '',
      city: address.city ?? '',
      state: address.state ?? '',
      postalCode: address.postalCode ?? ''
    },
    name: source.name,
    description: source.description ?? '',
    website: source.website ?? '',
    dunsNumber: source.dunsNumber ?? '',
    lei: source.lei ?? '',
    ein: source.ein ?? '',
    vatNumber: source.vatNumber ?? '',
    parentOrganization: source.parentOrganization ?? '',
    size: source.size,
    allianzEhid: source.allianzEhid ?? '',
    traderId: source.traderId,
    traderName: source.traderName
  }
}

export function customerFormToCreateWire(form: CustomerForm): CustomerCreateWire {
  const wire: CustomerCreateWire = {
    address: {
      country: form.address.country
    },
    name: form.name.trim()
  }

  const line1 = optionalText(form.address.line1)
  if (line1 !== undefined) wire.address.line1 = line1
  const line2 = optionalText(form.address.line2)
  if (line2 !== undefined) wire.address.line2 = line2
  const city = optionalText(form.address.city)
  if (city !== undefined) wire.address.city = city
  const state = optionalText(form.address.state)
  if (state !== undefined) wire.address.state = state
  const postalCode = optionalText(form.address.postalCode)
  if (postalCode !== undefined) wire.address.postalCode = postalCode

  const description = optionalText(form.description)
  if (description !== undefined) wire.description = description
  if (form.size != null) wire.size = form.size
  const website = optionalText(form.website)
  if (website !== undefined) wire.website = website
  const dunsNumber = optionalText(form.dunsNumber)
  if (dunsNumber !== undefined) wire.dunsNumber = dunsNumber
  const lei = optionalText(form.lei)
  if (lei !== undefined) wire.lei = lei
  const ein = optionalText(form.ein)
  if (ein !== undefined) wire.ein = ein
  const vatNumber = optionalText(form.vatNumber)
  if (vatNumber !== undefined) wire.vatNumber = vatNumber
  const parentOrganization = optionalText(form.parentOrganization)
  if (parentOrganization !== undefined) wire.parentOrganization = parentOrganization
  const allianzEhid = optionalText(form.allianzEhid)
  if (allianzEhid !== undefined) wire.allianzEhid = allianzEhid

  return wire
}

export function customerFormToUpdateWire(form: CustomerForm, dirty: CustomerFormDirty): CustomerUpdateWire {
  const wire: CustomerUpdateWire = {}
  const address: NonNullable<CustomerUpdateWire['address']> = {}

  if (isAddressDirty(dirty, 'country')) address.country = form.address.country
  if (isAddressDirty(dirty, 'line1')) address.line1 = clearableText(form.address.line1)
  if (isAddressDirty(dirty, 'line2')) address.line2 = clearableText(form.address.line2)
  if (isAddressDirty(dirty, 'city')) address.city = clearableText(form.address.city)
  if (isAddressDirty(dirty, 'state')) address.state = clearableText(form.address.state)
  if (isAddressDirty(dirty, 'postalCode')) address.postalCode = clearableText(form.address.postalCode)
  if (!patchIsEmpty(address)) wire.address = address

  if (isDirty(dirty, 'name')) wire.name = form.name.trim()
  if (isDirty(dirty, 'description')) wire.description = clearableText(form.description)
  if (isDirty(dirty, 'size')) wire.size = form.size ?? null
  if (isDirty(dirty, 'website')) wire.website = clearableText(form.website)
  if (isDirty(dirty, 'dunsNumber')) wire.dunsNumber = clearableText(form.dunsNumber)
  if (isDirty(dirty, 'lei')) wire.lei = clearableText(form.lei)
  if (isDirty(dirty, 'ein')) wire.ein = clearableText(form.ein)
  if (isDirty(dirty, 'vatNumber')) wire.vatNumber = clearableText(form.vatNumber)
  if (isDirty(dirty, 'parentOrganization')) wire.parentOrganization = clearableText(form.parentOrganization)
  if (isDirty(dirty, 'allianzEhid')) wire.allianzEhid = clearableText(form.allianzEhid)

  return wire
}
